/**
 * React Query hooks for prospect timeline and sequence status
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { auth } from '@/lib/firebase';
import type { TimelineResponse, SequenceStatusResponse } from '@/types/events';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'https://sales-api-768596480524.us-central1.run.app';

async function getAuthHeader(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();
  return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
}

async function fetchTimeline(prospectId: string): Promise<TimelineResponse> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/prospects/${prospectId}/timeline`, { headers });
  if (!res.ok) throw new Error('Failed to fetch timeline');
  return res.json();
}

async function fetchSequenceStatus(prospectId: string): Promise<SequenceStatusResponse> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/prospects/${prospectId}/sequence-status`, { headers });
  if (!res.ok) throw new Error('Failed to fetch sequence status');
  return res.json();
}

export function useProspectTimeline(prospectId: string | null) {
  return useQuery({
    queryKey: ['timeline', prospectId],
    queryFn: () => fetchTimeline(prospectId!),
    enabled: !!prospectId,
    staleTime: 60000, // 1 minute
  });
}

export function useSequenceStatus(prospectId: string | null) {
  return useQuery({
    queryKey: ['sequence-status', prospectId],
    queryFn: () => fetchSequenceStatus(prospectId!),
    enabled: !!prospectId,
    staleTime: 30000,
  });
}

type SequenceAction = 'pause' | 'resume' | 'opt-out' | 'nurture';

async function postSequenceAction(prospectId: string, action: SequenceAction): Promise<unknown> {
  const headers = await getAuthHeader();
  const res = await fetch(`${API_BASE_URL}/api/prospects/${prospectId}/sequence/${action}`, {
    method: 'POST',
    headers,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ detail: res.statusText }));
    throw new Error(err.detail || `Failed to ${action} sequence`);
  }
  return res.json();
}

export function useSequenceAction(prospectId: string) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (action: SequenceAction) => postSequenceAction(prospectId, action),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sequence-status', prospectId] });
    },
  });
}
